"use client";

import { useEffect, useState } from "react";

export interface EngagementTrackingInputs {
  /** True only while this card's sound is the globally playing one. */
  isThisSoundPlaying: boolean;
  /** Global player clock — only meaningful while isThisSoundPlaying. */
  currentTime: number;
  duration: number;
  /** Playback position as a percentage (0-100). */
  progress: number;
}

/**
 * One-shot engagement flags for a sound card, derived from the global
 * playback clock (TES-829 engaged preview + W3-1 preview completion).
 * Both flags are sticky for the card's lifetime.
 */
export function useEngagementTracking({
  isThisSoundPlaying,
  currentTime,
  duration,
  progress,
}: EngagementTrackingInputs) {
  // TES-829: contextual download CTA. On the card/listing surfaces
  // (library + home) the preview→download_button_click rate is ~0.10%
  // vs 5.66% on the sound detail page — the bare 44×44 download icon
  // reads as chrome, not the install action. Once the visitor has
  // engaged-previewed this sound (played ≥2s, or ≥50% of a sub-2s meme
  // clip since Tesla caps lock sounds at 5s) we treat that as install
  // intent and expand the icon into a legible labeled CTA. Sticky for
  // the card's lifetime so it doesn't flip back when playback moves on.
  const [hasEngagedPreview, setHasEngagedPreview] = useState(false);

  // TES-829: mark install intent once this card's sound has been
  // previewed long enough to count as engagement. currentTime/duration
  // are global but only meaningful while this sound is the active one,
  // so the read is gated on isThisSoundPlaying.
  useEffect(() => {
    if (hasEngagedPreview || !isThisSoundPlaying) return;
    const threshold = duration > 0 ? Math.min(2, duration * 0.5) : 2;
    // One-way sticky flag driven by the external audio clock; the
    // early-return guard makes this fire at most once, so there is no
    // cascading-render risk. Same accepted pattern as
    // audio-player-context.tsx for syncing from an external system.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot transition from external playback clock, guarded against re-fire
    if (currentTime >= threshold) setHasEngagedPreview(true);
  }, [hasEngagedPreview, isThisSoundPlaying, currentTime, duration]);

  // W3-1 Experiment 1: preview completion CTA.
  // Promoted once the user has listened to ≥80% of this sound (same
  // threshold as the funnel "preview_completed" event). Sticky for the
  // card lifetime so the CTA persists after playback ends. Mirrors the
  // same guarded one-shot pattern as hasEngagedPreview above.
  const [previewCompleted, setPreviewCompleted] = useState(false);
  useEffect(() => {
    if (previewCompleted || !isThisSoundPlaying) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot transition from external playback progress, guarded against re-fire
    if (progress >= 80) setPreviewCompleted(true);
  }, [previewCompleted, isThisSoundPlaying, progress]);

  return { hasEngagedPreview, previewCompleted };
}
